import { BackendCompatibleLocales, SemanticNameEnum } from '../../enums';
import { z, ZodObject } from 'zod';
import { NullablePhoneNumberPartsOptionalValidatorSchema } from '../shared/phoneNumber.schema';

export const GuestValidatorSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  guestEmail: z.string().min(1),
  mobilePhoneNumber: NullablePhoneNumberPartsOptionalValidatorSchema,
  locale: z.enum(BackendCompatibleLocales),
});

const GuestCustomInfoValidatorSchema = z.object({
  company: z.string(),
  jobtitle: z.string(),
});

export const CustomInfoValidator = z.object({
  informationValue: z.boolean().or(z.string()),
  semanticName: z.enum(SemanticNameEnum),
});

// `.extend` rather than the deprecated `.merge`: zod 4's `merge` loses the
// merged shape, which collapses the inferred value type to `{}`.
export const GuestFormValidatorSchema = GuestValidatorSchema.extend(
  GuestCustomInfoValidatorSchema.shape
);

export type GuestFormValidator = z.infer<typeof GuestFormValidatorSchema>;

export type GuestValidator = z.infer<typeof GuestValidatorSchema>;

export type CustomInfoValidator = z.infer<typeof CustomInfoValidator>;

/**
 * The returned schema validates the fan group's custom-information fields,
 * whose keys are only known at runtime. The type deliberately carries an empty
 * shape: an index signature here would swallow the specific keys of any schema
 * this is `.extend`ed into, collapsing the guest fields to `string | undefined`.
 * The custom fields are still validated — they are just not statically typed.
 */
export function generateConditionalGuestCustomInfoValidatorSchema(
  fields?: { name: string; isRequired: boolean }[]
): ZodObject<Record<never, never>> {
  let schema = z.object({});
  if (!fields) return schema;
  fields.forEach((field) => {
    if (field.isRequired) {
      schema = z.object({
        ...schema.shape,
        [field.name]: z.string().min(1),
      });
    } else {
      schema = z.object({
        ...schema.shape,
        [field.name]: z.string().optional(),
      });
    }
  });
  return schema;
}
